import { chakra } from '@chakra-ui/react';
import { useMemo } from 'react';
import { Text } from '../primitives/Text';

/**
 * A horizontal bar chart / ranked breakdown (§14 dashboard — "runs by agent"). Built from
 * tokenized HTML rather than SVG: horizontal bars are the one chart shape where a filled
 * `<div>` track is both simpler and MORE accessible than a plot — every row carries its
 * label and value as real text, so it reads without color. The bar itself is decorative
 * reinforcement (default EHE brand blue), theme-aware via design tokens.
 *
 * Bars are proportional to the largest value in the set. `maxBars` caps the list so a long
 * tail never dominates the card; the caller decides what "top N" means.
 */

export interface BarChartDatum {
  /** Stable key + accessible label. */
  id: string;
  label: string;
  value: number;
  /** Optional per-bar color override (else the shared `color`). */
  color?: string;
}

export interface BarChartProps {
  data: BarChartDatum[];
  /** Bar fill (brand hue). Default = EHE brand blue. */
  color?: string;
  formatValue?: (n: number) => string;
  /** Show at most this many bars (largest first is the caller's responsibility). */
  maxBars?: number;
  /** Accessible group label. */
  ariaLabel: string;
  'data-testid'?: string;
}

const Row = chakra('div', {
  base: { display: 'flex', flexDirection: 'column', gap: '1.5' },
});

const Track = chakra('div', {
  base: {
    position: 'relative',
    height: '2',
    borderRadius: 'full',
    bg: 'surface.subtle',
    overflow: 'hidden',
  },
});

const Fill = chakra('div', {
  base: {
    position: 'absolute',
    insetInlineStart: 0,
    top: 0,
    bottom: 0,
    borderRadius: 'full',
    transitionProperty: 'width',
    transitionDuration: 'slow',
    transitionTimingFunction: 'standard',
  },
});

const List = chakra('div', {
  base: { display: 'flex', flexDirection: 'column', gap: '4' },
});

function defaultFormat(n: number): string {
  return n.toLocaleString();
}

export function BarChart({
  data,
  color = '#005ff3',
  formatValue = defaultFormat,
  maxBars = 6,
  ariaLabel,
  'data-testid': testid,
}: BarChartProps) {
  const rows = useMemo(() => {
    const shown = data.slice(0, maxBars);
    const max = Math.max(1, ...shown.map((d) => d.value));
    return shown.map((d) => ({ ...d, pct: Math.max(2, Math.round((d.value / max) * 100)) }));
  }, [data, maxBars]);

  return (
    <List role="group" aria-label={ariaLabel} data-testid={testid}>
      {rows.map((r) => (
        <Row key={r.id} data-testid="bar-chart-row">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
            <Text size="sm" weight="medium" truncate>
              {r.label}
            </Text>
            <Text as="span" size="sm" tone="secondary" mono>
              {formatValue(r.value)}
            </Text>
          </div>
          <Track>
            <Fill style={{ width: `${r.pct}%`, backgroundColor: r.color ?? color }} />
          </Track>
        </Row>
      ))}
    </List>
  );
}
